<template>
  <div class="package-details">
    <div class="page-header">
      <h1 class="page-title">{{ $t("subscriptions.packageDetails") }}</h1>
      <!-- <the-breadcrumb :items="breadcrumbItems" /> -->
    </div>

    <div class="content-wrapper">
      <div v-if="loading" class="loading-container">
        <v-progress-circular indeterminate color="primary" />
      </div>

      <div v-else-if="packageData" class="details-card">
        <div class="details-grid">
          <div class="image-section">
            <img :src="packageData.image" :alt="packageData.name" />
          </div>

          <div class="info-section">
            <h2 class="package-title">{{ packageData.name }}</h2>

            <div class="price-info">
              <div class="price-main">
                <span class="label">{{ $t("subscriptions.totalPrice") }}</span>
                <span class="price"
                  >{{ packageData.total_with_tax }}
                  {{ $t("common.currency") }}</span
                >
              </div>

              <div
                v-if="packageData.price_after_discount"
                class="price-breakdown"
              >
                <div class="price-item">
                  <span>{{ $t("subscriptions.originalPrice") }}</span>
                  <span class="strikethrough"
                    >{{ packageData.price }} {{ $t("common.currency") }}</span
                  >
                </div>
                <div class="price-item">
                  <span>{{ $t("subscriptions.afterDiscount") }}</span>
                  <span class="highlight"
                    >{{ packageData.price_after_discount }}
                    {{ $t("common.currency") }}</span
                  >
                </div>
              </div>

              <div class="price-breakdown">
                <div class="price-item">
                  <span>{{ $t("subscriptions.basePrice") }}</span>
                  <span
                    >{{ packageData.paid_price }}
                    {{ $t("common.currency") }}</span
                  >
                </div>
                <div class="price-item">
                  <span
                    >{{ $t("subscriptions.tax") }} ({{
                      packageData.tax_value
                    }}%)</span
                  >
                  <span
                    >{{
                      (
                        packageData.total_with_tax - packageData.paid_price
                      ).toFixed(2)
                    }}
                    {{ $t("common.currency") }}</span
                  >
                </div>
              </div>
            </div>

            <div class="package-features">
              <div class="feature-item">
                <i class="fas fa-calendar-alt"></i>
                <div class="feature-content">
                  <span class="feature-label">{{
                    $t("subscriptions.duration")
                  }}</span>
                  <span class="feature-value"
                    >{{ packageData.duration }}
                    {{ $t("subscriptions.months") }}</span
                  >
                </div>
              </div>

              <div
                v-if="packageData.description"
                class="feature-item full-width"
              >
                <i class="fas fa-info-circle"></i>
                <div class="feature-content">
                  <span class="feature-label">{{
                    $t("subscriptions.description")
                  }}</span>
                  <p class="feature-value">{{ packageData.description }}</p>
                </div>
              </div>
            </div>

            <div class="action-buttons">
              <base-button
                :btnText="$t('common.back')"
                styleType="cancel_btn"
                @fireClick="goBack"
              />
              <base-button
                :btnText="$t('subscriptions.purchasePackage')"
                styleType="primary_btn"
                @fireClick="goToPayment"
              />
            </div>
          </div>
        </div>
      </div>

      <div v-else class="no-data">
        <img
          src="@/assets/media/empty_messages/search_message.png"
          alt="Not found"
        />
        <h3>{{ $t("subscriptions.packageNotFound") }}</h3>
        <base-button
          :btnText="$t('common.back')"
          styleType="primary_btn"
          @fireClick="goBack"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import TheBreadcrumb from "@/components/structure/TheBreadcrumb.vue";

export default {
  name: "PackageDetails",

  components: {
    TheBreadcrumb,
  },

  data() {
    return {
      loading: false,
      packageData: null,
    };
  },

  computed: {
    ...mapGetters("subscriptions", ["selectedPackage"]),

    breadcrumbItems() {
      return [
        { text: this.$t("common.home"), to: "/" },
        {
          text: this.$t("subscriptions.title"),
          to: { name: "CurrentSubscription" },
        },
        {
          text: this.$t("subscriptions.availablePackages"),
          to: { name: "PackagesList" },
        },
        { text: this.$t("subscriptions.packageDetails"), active: true },
      ];
    },
  },

  mounted() {
    this.loadPackageDetails();
  },

  methods: {
    ...mapActions("subscriptions", ["fetchPackageDetails"]),

    async loadPackageDetails() {
      const packageId = this.$route.params.id;
      this.loading = true;

      try {
        const response = await this.fetchPackageDetails(packageId);
        this.packageData = response.data.data;
      } catch (error) {
        this.$message.error(
          error.message || this.$t("subscriptions.errors.loadFailed")
        );
      } finally {
        this.loading = false;
      }
    },

    goToPayment() {
      this.$router.push({
        name: "PackagePayment",
        params: { id: this.$route.params.id },
      });
    },

    goBack() {
      this.$router.back();
    },
  },
};
</script>

<style lang="scss" scoped>
.package-details {
  padding: 20px;

  .page-header {
    margin-bottom: 30px;

    .page-title {
      font-size: 24px;
      font-weight: 600;
      margin-bottom: 10px;
      color: var(--theme_text_clr);
    }
  }

  .content-wrapper {
    .loading-container {
      display: flex;
      justify-content: center;
      align-items: center;
      min-height: 400px;
    }

    .details-card {
      background: var(--theme_second_bg_clr);
      border-radius: 12px;
      box-shadow: 0 2px 8px var(--simple_shadow_clr);
      overflow: hidden;

      .details-grid {
        display: grid;
        grid-template-columns: 400px 1fr;
        gap: 40px;
        padding: 40px;

        @media (max-width: 968px) {
          grid-template-columns: 1fr;
          gap: 30px;
        }

        .image-section {
          img {
            width: 100%;
            height: auto;
            border-radius: 8px;
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
          }
        }

        .info-section {
          display: flex;
          flex-direction: column;
          gap: 30px;

          .package-title {
            font-size: 28px;
            font-weight: 700;
            color: var(--theme_text_clr);
            margin: 0;
          }

          .price-info {
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            padding: 25px;
            border-radius: 8px;
            color: #fff;

            .price-main {
              display: flex;
              justify-content: space-between;
              align-items: center;
              margin-bottom: 15px;
              padding-bottom: 15px;
              border-bottom: 1px solid rgba(255, 255, 255, 0.2);

              .label {
                font-size: 16px;
                font-weight: 500;
              }

              .price {
                font-size: 32px;
                font-weight: 700;
              }
            }

            .price-breakdown {
              display: flex;
              flex-direction: column;
              gap: 10px;
              margin-top: 15px;

              .price-item {
                display: flex;
                justify-content: space-between;
                font-size: 14px;

                .strikethrough {
                  text-decoration: line-through;
                  opacity: 0.7;
                }

                .highlight {
                  background: rgba(255, 255, 255, 0.2);
                  padding: 2px 8px;
                  border-radius: 4px;
                  font-weight: 600;
                }
              }
            }
          }

          .package-features {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 20px;

            @media (max-width: 768px) {
              grid-template-columns: 1fr;
            }

            .feature-item {
              display: flex;
              gap: 15px;
              padding: 20px;
              background: var(--theme_main_bg_clr);
              border-radius: 8px;

              &.full-width {
                grid-column: 1 / -1;
              }

              i {
                color: var(--main_theme_clr);
                font-size: 24px;
                margin-top: 5px;
              }

              .feature-content {
                flex: 1;

                .feature-label {
                  display: block;
                  font-size: 14px;
                  font-weight: 600;
                  color: var(--theme_secondary_text_clr);
                  margin-bottom: 5px;
                }

                .feature-value {
                  display: block;
                  font-size: 16px;
                  color: var(--theme_text_clr);
                  font-weight: 500;
                }

                p.feature-value {
                  line-height: 1.6;
                  margin: 0;
                }
              }
            }
          }

          .action-buttons {
            display: flex;
            gap: 15px;
            justify-content: flex-end;

            @media (max-width: 768px) {
              flex-direction: column;
            }
          }
        }
      }
    }

    .no-data {
      text-align: center;
      padding: 60px 20px;
      background: var(--theme_second_bg_clr);
      border-radius: 12px;
      box-shadow: 0 2px 8px var(--simple_shadow_clr);

      img {
        width: 200px;
        margin-bottom: 30px;
      }

      h3 {
        font-size: 22px;
        font-weight: 600;
        color: var(--theme_text_clr);
        margin-bottom: 30px;
      }
    }
  }
}
</style>
